import { UnitTypes, ItemTypes, StructureTypes } from "../../Network";

export const TILE_WIDTH = 32;
export const TILE_HEIGHT = 32;

export enum Scenes {
  Main = "Main",
  UI = "UI",
}

export enum Maps {
  Main = "Main",
  Pixel = "Pixel",
  Tactic = "Tactic",
  Strategic = "Strategic",
}

export enum Assets {
  OverworldTileset = "OverworldTileset",
  MainAtlas = "MainAtlas",
  Background = "Background",
  TiledBackground = "TiledBackground",
  CloudBackground = "CloudBackground",
  Cloud1 = "Cloud1",
  Cloud2 = "Cloud2",
  IslandDepth = "IslandDepth",
  Professor = "Professor",
  Splash = "Splash",
}

export enum Sprites {
  Settlement,
  SpawnSettlement,
  Gold,
  Container,
  GoldShrine,
  EmberCrown,
  Portal,
  Donkey,
  Soldier,
  Spear,
  SelectUI,
  Longbowman,
  Ballista,
  SummoningAltar,
  WoodenWall,
  Crystal,

  Zero,
  One,
  Two,
  Three,
  Four,
  Five,
  Six,
  Seven,
  Eight,
  Nine,

  Chain,
  Cog,

  ZeroCircle,
  OneCircle,
  TwoCircle,
  ThreeCircle,

  Swordsman,
  Pikeman,
  Golem,

  Archer,
  Catapult,
  Wizard,

  Rider,
  Knight,
  Dragon,

  HealthBar,
  StaminaBar,
  ResourceBar,

  GreenTick,
  YellowTick,
  OrangeTick,
  RedTick,

  CombatPreview,
  CrossedSwords,
  PercentGreen,
  PercentRed,
  BuildFrame,
  UnitFrame,

  ZeroGreen,
  OneGreen,
  TwoGreen,
  ThreeGreen,
  FourGreen,
  FiveGreen,
  SixGreen,
  SevenGreen,
  EightGreen,
  NineGreen,

  ZeroRed,
  OneRed,
  TwoRed,
  ThreeRed,
  FourRed,
  FiveRed,
  SixRed,
  SevenRed,
  EightRed,
  NineRed,
}

export enum Animations {
  //UI
  ChainCarousel = "ChainCarousel",

  // Structures
  Portal = "Portal",
  GoldShrine = "GoldShrine",
  ChargingCrystal = "ChargingCrystal",
  SummoningAltar = "SummoningAltar",

  // Units
  SwordsmanIdle = "SwordsmanIdle",
  PikemanIdle = "PikemanIdle",
  GolemIdle = "GolemIdle",

  ArcherIdle = "ArcherIdle",
  CatapultIdle = "CatapultIdle",
  WizardIdle = "WizardIdle",

  RiderIdle = "RiderIdle",
  KnightIdle = "KnightIdle",
  DragonIdle = "DragonIdle",

  SwordsmanAttack = "SwordsmanAttack",
  PikemanAttack = "PikemanAttack",
  GolemAttack = "GolemAttack",

  ArcherAttack = "ArcherAttack",
  CatapultAttack = "CatapultAttack",
  WizardAttack = "WizardAttack",

  RiderAttack = "RiderAttack",
  KnightAttack = "KnightAttack",
  DragonAttack = "DragonAttack",

  SwordsmanDeath = "SwordsmanDeath",
  PikemanDeath = "PikemanDeath",
  GolemDeath = "GolemDeath",

  ArcherDeath = "ArcherDeath",
  CatapultDeath = "CatapultDeath",
  WizardDeath = "WizardDeath",

  RiderDeath = "RiderDeath",
  KnightDeath = "KnightDeath",
  DragonDeath = "DragonDeath",

  // Items
  EmberCrown = "EmberCrown",
  BlazingHeart = "BlazingHeart",
}

function createWalkAnimations() {
  return [
    UnitTypes.Swordsman,
    UnitTypes.Pikeman,
    UnitTypes.Golem,
    UnitTypes.Archer,
    UnitTypes.Catapult,
    UnitTypes.Rider,
    UnitTypes.Knight,
  ].reduce((anims, unitType) => {
    anims[unitType] = [`${unitType}WalkUp`, `${unitType}WalkDown`, `${unitType}WalkLeft`, `${unitType}WalkRight`];

    return anims;
  }, {} as Record<UnitTypes, [string, string, string, string]>);
}

export const WALK_ANIMATIONS = createWalkAnimations();

export const UnitTypeSprites: Record<number, Sprites> = {
  [UnitTypes.Unknown]: Sprites.Gold,

  [UnitTypes.Swordsman]: Sprites.Swordsman,
  [UnitTypes.Pikeman]: Sprites.Pikeman,
  [UnitTypes.Golem]: Sprites.Golem,

  [UnitTypes.Archer]: Sprites.Archer,
  [UnitTypes.Catapult]: Sprites.Catapult,
  [UnitTypes.Wizard]: Sprites.Wizard,

  [UnitTypes.Rider]: Sprites.Rider,
  [UnitTypes.Knight]: Sprites.Knight,
  [UnitTypes.Dragon]: Sprites.Dragon,
};

export const ItemTypeSprites: Record<number, Sprites> = {
  [ItemTypes.Gold]: Sprites.Gold,
  [ItemTypes.EmberCrown]: Sprites.EmberCrown,
};

export const StructureTypeSprites: Record<number, Sprites> = {
  [StructureTypes.Settlement]: Sprites.Settlement,
  [StructureTypes.SpawnSettlement]: Sprites.SpawnSettlement,
  [StructureTypes.GoldShrine]: Sprites.GoldShrine,
  [StructureTypes.BlazingHeartShrine]: Sprites.GoldShrine,
  [StructureTypes.EscapePortal]: Sprites.Portal,
  [StructureTypes.Portal]: Sprites.Portal,
  [StructureTypes.Container]: Sprites.Container,
  [StructureTypes.SummoningAltar]: Sprites.SummoningAltar,
  [StructureTypes.WoodenWall]: Sprites.WoodenWall,
  [StructureTypes.ChargingCrystal]: Sprites.Crystal,
};

export const UnitTypeAnimations: Record<number, Animations> = {
  [UnitTypes.Swordsman]: Animations.SwordsmanIdle,
  [UnitTypes.Pikeman]: Animations.PikemanIdle,
  [UnitTypes.Golem]: Animations.GolemIdle,

  [UnitTypes.Archer]: Animations.ArcherIdle,
  [UnitTypes.Catapult]: Animations.CatapultIdle,
  [UnitTypes.Wizard]: Animations.WizardIdle,

  [UnitTypes.Rider]: Animations.RiderIdle,
  [UnitTypes.Knight]: Animations.KnightIdle,
  [UnitTypes.Dragon]: Animations.DragonIdle,
};

export const UnitTypeAttackAnimations: Record<number, Animations> = {
  [UnitTypes.Swordsman]: Animations.SwordsmanAttack,
  [UnitTypes.Pikeman]: Animations.PikemanAttack,
  [UnitTypes.Golem]: Animations.GolemAttack,

  [UnitTypes.Archer]: Animations.ArcherAttack,
  [UnitTypes.Catapult]: Animations.CatapultAttack,
  [UnitTypes.Wizard]: Animations.WizardAttack,

  [UnitTypes.Rider]: Animations.RiderAttack,
  [UnitTypes.Knight]: Animations.KnightAttack,
  [UnitTypes.Dragon]: Animations.DragonAttack,
};

export const UnitTypeDeathAnimations: Record<number, Animations> = {
  [UnitTypes.Swordsman]: Animations.SwordsmanDeath,
  [UnitTypes.Pikeman]: Animations.PikemanDeath,
  [UnitTypes.Golem]: Animations.GolemDeath,

  [UnitTypes.Archer]: Animations.ArcherDeath,
  [UnitTypes.Catapult]: Animations.CatapultDeath,
  [UnitTypes.Wizard]: Animations.WizardDeath,

  [UnitTypes.Rider]: Animations.RiderDeath,
  [UnitTypes.Knight]: Animations.KnightDeath,
  [UnitTypes.Dragon]: Animations.DragonDeath,
};

export const StructureTypeAnimations: Record<number, Animations> = {
  [StructureTypes.EscapePortal]: Animations.Portal,
  [StructureTypes.Portal]: Animations.Portal,
  [StructureTypes.BlazingHeartShrine]: Animations.GoldShrine,
  [StructureTypes.ChargingCrystal]: Animations.ChargingCrystal,
  [StructureTypes.SummoningAltar]: Animations.SummoningAltar,
};

export const ItemTypeAnimations: Record<number, Animations> = {
  [ItemTypes.EmberCrown]: Animations.EmberCrown,
  [ItemTypes.BlazingHeart]: Animations.BlazingHeart,
};
